import { useEffect, useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { PARTY } from "../config/party";
import { getContent, saveContent } from "../lib/content";
import { isAlly } from "../lib/theme";

const TARGET = new Date(PARTY.dateISO);

const DATE_LABEL = (() => {
  const t = new Date(PARTY.dateISO);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(t.getDate())}.${p(t.getMonth() + 1)}.${String(t.getFullYear()).slice(2)} - ${p(t.getHours())}:${p(t.getMinutes())}`;
})();

const DEFAULT_TEXTS = {
  eyebrow: "Intercepted transmission // Ligma Sugma Psi // 132 BPM",
  subtitle: "The rats demand your presence. 24.09 - Fitzroy North.",
  subtitle_ally:
    "Same rats. More flavour. The pit tastes like rainbows tonight. Still strictly no cats.",
};

const pad = (n) => String(n).padStart(2, "0");

const useCountdown = () => {
  const [left, setLeft] = useState(TARGET - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(TARGET - Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const s = Math.max(0, Math.floor(left / 1000));
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
};

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { d, h, m, s } = useCountdown();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [7, -7]);
  const rotateY = useTransform(mx, [0, 1], [-9, 9]);
  const [texts, setTexts] = useState(DEFAULT_TEXTS);
  const [editing, setEditing] = useState(false);
  const [ally, setAllyState] = useState(isAlly);

  useEffect(() => {
    getContent("hero").then((v) => {
      if (v) setTexts((t) => ({ ...t, ...v }));
    });
  }, []);

  useEffect(() => {
    const onEdit = (e) => setEditing(e.detail.editing);
    const onTheme = (e) => setAllyState(e.detail.ally);
    window.addEventListener("rotu:edit", onEdit);
    window.addEventListener("rotu:theme", onTheme);
    return () => {
      window.removeEventListener("rotu:edit", onEdit);
      window.removeEventListener("rotu:theme", onTheme);
    };
  }, []);

  const saveField = (field, el) => {
    const value = el.innerText.trim();
    if (!value || value === texts[field]) return;
    const next = { ...texts, [field]: value };
    setTexts(next);
    saveContent("hero", next);
  };

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      onPointerMove={(e) => {
        mx.set(e.clientX / window.innerWidth);
        my.set(e.clientY / window.innerHeight);
      }}
      data-testid="hero-section"
    >
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1763757575623-ffafe0867aa3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#08080C]/70 via-[#08080C]/40 to-[#08080C]" />

      <motion.div
        className="relative z-10 px-6 sm:px-12 lg:px-24 pt-24 pb-28"
        style={{ rotateX, rotateY, transformPerspective: 900 }}
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className={`text-[11px] sm:text-xs tracking-[0.5em] text-[#00F0FF] uppercase mb-6 ${editing ? "editable-box" : ""}`}
          data-testid="hero-eyebrow"
          contentEditable={editing}
          suppressContentEditableWarning
          onBlur={(e) => saveField("eyebrow", e.currentTarget)}
        >
          {texts.eyebrow}
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-[8.5vw] sm:text-6xl lg:text-[7.5rem] bass-text" data-testid="hero-title">
          <Line delay={0.35}>
            <span className="text-white">Rats of</span>
          </Line>
          <Line delay={0.5}>
            <span className="text-stroke-lime">the</span>
          </Line>
          <Line delay={0.65}>
            <span className="glitchy horror-flicker text-[#39FF14]">Underground</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className={`mt-8 max-w-xl text-sm sm:text-base text-zinc-400 leading-relaxed ${editing ? "editable-box" : ""}`}
          data-testid="hero-subtitle"
          contentEditable={editing}
          suppressContentEditableWarning
          onBlur={(e) => saveField(ally ? "subtitle_ally" : "subtitle", e.currentTarget)}
        >
          {ally ? texts.subtitle_ally : texts.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-end gap-8"
        >
          <div className="border border-white/10 bg-[#08080C]/70 backdrop-blur-xl px-6 py-5" data-testid="hero-countdown">
            <div className="text-[10px] tracking-[0.35em] text-zinc-500 uppercase mb-2">
              Rat Revolution
            </div>
            <div className="font-display text-3xl sm:text-4xl font-bold text-white tabular-nums">
              {pad(d)}<span className="text-[#FF0055]">:</span>{pad(h)}
              <span className="text-[#FF0055]">:</span>{pad(m)}
              <span className="text-[#FF0055]">:</span>{pad(s)}
            </div>
            <div className="text-[10px] tracking-[0.35em] text-[#FFE600] uppercase mt-2" data-testid="hero-date-label">
              {DATE_LABEL}
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-zinc-600"
      >
        <ChevronDown className="animate-bounce" size={20} />
      </motion.div>
    </section>
  );
};
